import React, { useState, useEffect } from 'react'
import {
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Clock,
  Target,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Info,
  ExternalLink,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Coins
} from 'lucide-react'
import { cn } from '../lib/utils'
import { PoolStats, UIContributor } from '../lib/poolDataTransformers'

/**
 * Props for the PoolOverview component
 * poolStats and contributors are required - this component only displays live RPC data
 */
interface PoolOverviewProps {
  tokenMint: string
  poolStats: PoolStats
  contributors: UIContributor[]
  className?: string
  showDetailedStats?: boolean
  onRefresh?: () => void
  isRefreshing?: boolean
}

/**
 * PoolOverview Component - Premium glassmorphism pool dashboard
 * 
 * This is a PRESENTATION COMPONENT that displays live blockchain data.
 * It requires poolStats and contributors to be passed from the parent component.
 * Parent component (PoolPage.tsx) is responsible for fetching data via RPC.
 * 
 * Features:
 * - Real-time pool statistics with animated metrics
 * - Glassmorphism design with professional styling
 * - Interactive cards with hover effects and micro-animations
 * - Responsive grid layouts for different screen sizes
 * - V2 support with LST-based accounting (jitoSOL)
 * - Accessibility-compliant design patterns
 * 
 * Note (2026-01-13): Removed Fee Distribution card as it was not needed
 * Note (2026-01-19): Refactored to remove mock data - now requires live RPC data via props
 */
export function PoolOverview({
  tokenMint,
  poolStats,
  contributors,
  className,
  showDetailedStats = true,
  onRefresh,
  isRefreshing = false
}: PoolOverviewProps): JSX.Element {
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())

  // Update lastUpdated timestamp when poolStats changes
  useEffect(() => {
    setLastUpdated(new Date())
  }, [poolStats])

  /**
   * Format currency values for display (SOL values)
   */
  const formatCurrency = (value: number): string => {
    return `${value.toFixed(2)} SOL`
  }

  /**
   * Format percentage values
   */
  const formatPercentage = (value: number): string => {
    const sign = value >= 0 ? '+' : ''
    return `${sign}${value.toFixed(1)}%`
  }

  /**
   * Format wallet address for display
   */
  const formatAddress = (address: string): string => {
    return `${address.slice(0, 4)}...${address.slice(-4)}`
  }

  /**
   * Format time remaining from milliseconds
   */
  const formatTimeRemaining = (milliseconds: number): string => {
    const days = Math.floor(milliseconds / (24 * 60 * 60 * 1000))
    const hours = Math.floor((milliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000))
    return `${days}d ${hours}h`
  }

  /**
   * Get status display properties (color, icon, text) based on pool status
   */
  const getStatusDisplay = (status: PoolStats['status']) => {
    switch (status) {
      case 'active':
        return {
          color: 'text-status-success',
          bgColor: 'bg-status-success/10',
          borderColor: 'border-status-success/20',
          icon: CheckCircle2,
          text: 'Active'
        }
      case 'pending':
        return {
          color: 'text-status-warning',
          bgColor: 'bg-status-warning/10',
          borderColor: 'border-status-warning/20',
          icon: Clock,
          text: 'Pending'
        }
      case 'completed':
        return {
          color: 'text-status-info',
          bgColor: 'bg-status-info/10',
          borderColor: 'border-status-info/20',
          icon: Target,
          text: 'Completed'
        }
      case 'failed':
        return {
          color: 'text-status-error',
          bgColor: 'bg-status-error/10',
          borderColor: 'border-status-error/20',
          icon: AlertTriangle,
          text: 'Failed'
        }
    }
  }

  /**
   * MetricCard - Reusable card component for displaying a single metric
   * Displays an icon, value, optional change indicator, and description
   */
  interface MetricCardProps {
    title: string
    value: string | number
    change?: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon: React.ComponentType<any>
    description?: string
    trend?: 'up' | 'down' | 'neutral'
  }

  const MetricCard = ({ title, value, change, icon: Icon, description, trend = 'neutral' }: MetricCardProps) => {
    return (
      <div className="metric-card group hover:scale-102 transition-all duration-300 cursor-pointer">
        <div className="flex items-start justify-between mb-4">
          <div className={cn(
            "w-12 h-12 rounded-cyber flex items-center justify-center transition-all duration-300",
            "group-hover:shadow-neon-cyan group-hover:scale-110"
          )}>
            <div className="w-10 h-10 bg-cyber-cyan/20 rounded-cyber flex items-center justify-center">
              <Icon className="w-5 h-5 text-cyber-cyan" />
            </div>
          </div>
          
          {change !== undefined && (
            <div className={cn(
              "flex items-center space-x-1 px-2 py-1 rounded-cyber text-xs font-medium",
              trend === 'up' && "bg-status-success/20 text-status-success",
              trend === 'down' && "bg-status-error/20 text-status-error",
              trend === 'neutral' && "bg-dark-surface/50 text-text-muted"
            )}>
              {trend === 'up' && <ArrowUpRight className="w-3 h-3" />}
              {trend === 'down' && <ArrowDownRight className="w-3 h-3" />}
              <span>{formatPercentage(change)}</span>
            </div>
          )}
        </div>
        
        <div className="space-y-2">
          <div className="metric-value group-hover:text-glow-strong transition-all duration-300">
            {value}
          </div>
          <h3 className="text-sm font-medium text-text-secondary group-hover:text-text-primary transition-colors duration-300">
            {title}
          </h3>
          {description && (
            <p className="text-xs text-text-muted leading-relaxed">
              {description}
            </p>
          )}
        </div>
        
        {/* Card glow effect */}
        <div className="absolute inset-0 rounded-cyber bg-cyber-gradient opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none" />
      </div>
    )
  }

  const statusDisplay = getStatusDisplay(poolStats.status)
  const StatusIcon = statusDisplay.icon

  return (
    <div className={cn("space-y-4 lg:space-y-6", className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-cyber-gradient rounded-cyber flex items-center justify-center">
              <Coins className="w-6 h-6 text-dark-bg" />
            </div>
            <div>
              <h2 className="text-2xl font-header text-gradient-cyber">Pool Overview</h2>
              <div className="flex items-center space-x-2 text-sm text-text-muted">
                <span>Token:</span>
                <code className="font-mono bg-dark-surface px-2 py-1 rounded">
                  {formatAddress(tokenMint)}
                </code>
                <button 
                  className="hover:text-cyber-cyan transition-colors duration-200"
                  aria-label="View token on explorer"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Status Badge */}
          <div className={cn(
            "flex items-center space-x-2 px-3 py-2 rounded-cyber border",
            statusDisplay.bgColor,
            statusDisplay.borderColor
          )}>
            <StatusIcon className={cn("w-4 h-4", statusDisplay.color)} />
            <span className={cn("text-sm font-medium", statusDisplay.color)}>
              {statusDisplay.text}
            </span>
          </div>
          
          {/* Refresh Button - calls parent's onRefresh handler */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className={cn(
                "flex items-center justify-center w-10 h-10 rounded-cyber",
                "glass-dark hover:bg-dark-panel/70 transition-all duration-300",
                "focus-cyber interactive disabled:opacity-50 disabled:cursor-not-allowed"
              )}
              aria-label="Refresh pool data"
            >
              <RefreshCw className={cn(
                "w-4 h-4 text-text-secondary",
                isRefreshing && "animate-spin"
              )} />
            </button>
          )}
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <MetricCard
          title="Total Value Locked"
          value={formatCurrency(poolStats.totalValue)}
          change={poolStats.growthRate}
          trend={poolStats.growthRate >= 0 ? 'up' : 'down'}
          icon={DollarSign}
          description={`${poolStats.totalPoolTokens.toFixed(4)} jitoSOL`}
        />
        
        <MetricCard
          title="Contributors"
          value={poolStats.totalContributors}
          icon={Users}
          description="Active pool participants"
        />
        
        <MetricCard
          title="Active Proposals"
          value={poolStats.activeProposals}
          icon={Activity}
          description="Currently being voted on"
        />
        
        <MetricCard
          title="Success Rate"
          value={`${poolStats.successfulProposals > 0 ? Math.round((poolStats.successfulProposals / (poolStats.successfulProposals + 2)) * 100) : 0}%`}
          icon={Target}
          description="Successful proposal ratio"
        />
      </div>

      {/* Detailed Stats - UI FIX (2026-01-13): Removed Fee Distribution card */}
      {showDetailedStats && (
        <div className="grid md:grid-cols-2 gap-4 lg:gap-6">
          {/* Time & Performance */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-header text-cyber-cyan">Performance</h3>
              <Clock className="w-5 h-5 text-cyber-cyan" />
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-dark-border/30">
                <span className="text-text-secondary">Average Contribution</span>
                <span className="font-mono text-text-primary">
                  {formatCurrency(poolStats.averageContribution)}
                </span>
              </div>

              <div className="flex justify-between items-center py-3 border-b border-dark-border/30">
                <span className="text-text-secondary">Available jitoSOL</span>
                <span className="font-mono text-text-primary">
                  {poolStats.availablePoolTokens.toFixed(4)} jitoSOL
                </span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-dark-border/30">
                <span className="text-text-secondary">Growth Rate (24h)</span>
                <div className={cn(
                  "flex items-center space-x-2",
                  poolStats.growthRate >= 0 ? "text-status-success" : "text-status-error"
                )}>
                  {poolStats.growthRate >= 0 ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  <span className="font-mono">
                    {formatPercentage(poolStats.growthRate)}
                  </span>
                </div>
              </div>
              
              {poolStats.timeRemaining && poolStats.timeRemaining > 0 && (
                <div className="flex justify-between items-center py-3">
                  <span className="text-text-secondary">Time Remaining</span>
                  <span className="font-mono text-cyber-cyan">
                    {formatTimeRemaining(poolStats.timeRemaining)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Top Contributors */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-header text-cyber-magenta">Top Contributors</h3>
              <Users className="w-5 h-5 text-cyber-magenta" />
            </div>
            
            <div className="space-y-3">
              {contributors.length === 0 ? (
                <div className="text-center py-8 text-text-muted">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No contributors yet</p>
                </div>
              ) : (
                <>
                  {contributors.slice(0, 3).map((contributor, index) => (
                    <div
                      key={contributor.address}
                      className="flex items-center justify-between p-3 rounded-cyber hover:bg-dark-panel/30 transition-colors duration-200 overflow-hidden"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={cn(
                          "w-8 h-8 rounded-cyber flex items-center justify-center text-xs font-bold",
                          index === 0 && "bg-cyber-yellow/20 text-cyber-yellow",
                          index === 1 && "bg-cyber-cyan/20 text-cyber-cyan",
                          index === 2 && "bg-cyber-purple/20 text-cyber-purple"
                        )}>
                          #{index + 1}
                        </div>
                        <div>
                          <div className="font-mono text-sm text-text-primary">
                            {formatAddress(contributor.address)}
                          </div>
                          <div className="text-xs text-text-muted">
                            {contributor.percentage.toFixed(2)}% of pool
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="font-mono text-sm text-text-primary">
                          {contributor.amount.toFixed(4)} SOL
                        </div>
                        {contributor.isTopDonor && (
                          <div className="flex items-center space-x-1 text-xs text-cyber-green">
                            <Zap className="w-3 h-3" />
                            <span>Top</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {contributors.length > 3 && (
                    <button className="w-full py-3 text-sm text-cyber-cyan hover:text-cyber-magenta transition-colors duration-200 border border-dark-border/30 rounded-cyber hover:border-cyber-cyan/30">
                      View all {contributors.length} contributors
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Last Updated */}
      <div className="flex items-center justify-between text-xs text-text-muted">
        <div className="flex items-center space-x-2">
          <Info className="w-3 h-3" />
          <span>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-status-success rounded-full animate-pulse" />
          <span>Live data from blockchain</span>
        </div>
      </div>
    </div>
  )
}

export default PoolOverview
